import {
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import SubdirectoryArrowRight from '@mui/icons-material/SubdirectoryArrowRight';
import {
  Accordion,
  AccordionDetails,
  Box,
  IconButton,
  Menu,
  MenuItem,
  SelectChangeEvent,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TabProps,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import EmptyIcon from 'src/assets/images/NoDaypartEnabled.svg';
import { Header } from 'src/components/header';
import { ActionBar as ActionBarComponent } from 'src/components/header';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import { Column } from 'src/components/table';
import { StyledLocationSortButton } from 'src/components/table/CustomTable.styled';
import {
  CustomTableWithPagination,
  PAGINATION_HEIGHT,
} from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { RowsPerPageOptions } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { layoutTypeToPositionsKey } from 'src/constants';
import {
  DEFAULT_POSITION_ALL,
  getNoDaypartMessage,
  LISTVIEW_DROPDOWN_OPTIONS,
  LOCATION_HEADER_HEIGHT,
  LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT,
  NAVIGATION_STATE,
} from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { openSchedulePreview } from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/eventContentSidebar/EventContentSidebar.helper';
import { ActiveDayIndicators } from 'src/features/locations/calendarLocationPage/modules/ActiveDayIndicators';
import { LocationCalendarHeader } from 'src/features/locations/calendarLocationPage/modules/CalendarLocationHeader/LocationCalendarHeader';
import { AssemblyStatusChip } from 'src/features/locations/modules/AssemblyStatusChip';
import { DaypartDisabledAlert } from 'src/features/locations/modules/DaypartDisabledAlert';
import { StyledTab } from 'src/features/locations/scheduleLocationPageOO/modules/OOLocationSchedulePageTable/OOLocationSchedulePageTable.styled';
import {
  useGetLocationActiveDays,
  useMenu,
  useModal,
  useParamQuery,
} from 'src/hooks';
import { IMappedLocationGroupDaypart } from 'src/interfaces';
import { IScheduleItem } from 'src/interfaces/Schedule';
import { DataTestID, lang, useUserRole } from 'src/utils';

import { ActionBar } from '../actionBar/ActionBar';
import {
  generateDeleteScheduleOrItemPayload,
  getEarliestStartDate,
  getTableContainerHeight,
  isPreviewEnabled,
} from './LocationSchedulePageTable.helper';
import { useLocationScheduleTable } from './LocationSchedulePageTable.hook';
import {
  LocationSchedulePageTableProps,
  MenuContext,
  SelectedRow,
  VIEW_OPTIONS,
} from './LocationSchedulePageTable.interface';
import {
  ArrowIcon,
  BlankStateImg,
  BlankStateWrapper,
  ButtonGroupWrapper,
  Container,
  CustomTableCell,
  CustomTableCellSchedule,
  CustomTableContainer,
  DropdownContainer,
  FlexRowCenter,
  HeaderContainer,
  LocationOverrideChip,
  NoWrapTypography,
  OverrideEnabledChip,
  OverrideEnabledIcon,
  RoundedDropdown,
  SortArrowDownIconStyled,
  SortArrowUpIconStyled,
  SortIcons,
  StyledAccordionSummary,
  StyledExpandMoreIcon,
  StyledIconButton,
  StyledMenuItem,
  StyledPageMenuItem,
  StyledTable,
  StyledTableCell,
  TableCellContainer,
  TableHeadCell,
  Tabs,
  TopResultContainer,
} from './LocationSchedulePageTable.styled';

export const Tab = (props: TabProps) => {
  return (
    <CustomTooltip
      width={500}
      placement="bottom"
      title={props.label}
      overflowWidth={360}>
      <StyledTab {...props} />
    </CustomTooltip>
  );
};

export const LinkedTitleColumn = ({
  title,
  isdoublewidth,
  linkTo,
}: {
  title: string;
  isdoublewidth: string;
  linkTo: string;
}) => {
  return (
    <CustomTableCell $isdoublewidth={isdoublewidth} $isAssemblyName>
      <CustomTooltip
        width={500}
        placement="bottom"
        title={title}
        overflowLineNumber={1}>
        <Link to={linkTo} target="_blank">
          {title}
        </Link>
      </CustomTooltip>
    </CustomTableCell>
  );
};
export const CustomCellRenderer = {
  assemblyName: (row: any, column: Column) => (
    <LinkedTitleColumn
      key={column.id}
      title={row[column.id]}
      isdoublewidth={column.id}
      linkTo={PATHS.ASSEMBLY_DETAILS(row.assemblyData?.id)}
    />
  ),
  componentName: (row: any, column: Column) => {
    const componentId = row.component_id;

    return (
      <LinkedTitleColumn
        title={row[column.id]}
        isdoublewidth={column.id}
        linkTo={PATHS.COMPONENT_DETAILS(componentId)}
      />
    );
  },
  status: (row: any, column: Column) => (
    <AssemblyStatusChip status={row[column.id]} />
  ),
};
export const LocationSchedulePageTable = (
  props: LocationSchedulePageTableProps,
) => {
  const key = props.locationGroup?.layoutType?.name
    ? layoutTypeToPositionsKey[props.locationGroup?.layoutType?.name]
    : undefined;
  const totalPositions = key ? props.projectConfig?.[key] : undefined;
  const isDefaultAssembly =
    props.selectedOption === VIEW_OPTIONS.DEFAULT_ASSEMBLY;
  const selectedRowRef = useRef<SelectedRow>({} as any);
  const { sortTableData } = useLocationScheduleTable(
    props.tableData as IMappedLocationGroupDaypart[],
    props.sortDirection.isSortModified,
  );
  const { getParamsValue } = useParamQuery();
  const navigate = useNavigate();
  const sidebarDrawer = useModal();
  const publishModal = useModal();
  const showDeleteScheduleOrItemsModal = useModal();
  const location_group_id = getParamsValue('groupID').join();
  const { locationGroupId = '', locationCode = '' } = useParams();

  const isLocationPage = !!locationCode;
  const locationsCount = props.locationsCount ?? 0;

  const headers = props.columns.map((column) => column.label);

  const { data: activeDays = [] } = useGetLocationActiveDays(
    props.locationData?.location_code,
    props.locationGroup?.id,
  );

  const handleDropdownChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    props.onSelectedOptionChange(event.target.value as string);
  };
  const { isOwnerOperator, isViewOnly } = useUserRole();
  const [menuContext, setMenuContext] = useState<MenuContext | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const locationGroups = props.locationData?.locationGroups?.filter(
    (group) => !group.is_default,
  );
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const shouldShowMenuIcon = !isViewOnly || (isViewOnly && !isLocationPage);

  const handleToggle = (idx: number) => {
    setExpandedRow((prev) => (prev === idx ? null : idx));
  };
  const handleDeleteClick = () => {
    showDeleteScheduleOrItemsModal.onOpenModal(handleDeleteConfirm);
  };
  const handleEditClick = () => {
    navigate(
      {
        pathname: PATHS.LOCATIONS_SCHEDULE_COMPONENTS,
        search: `?scheduleId=${selectedRowRef.current?.scheduleId}`,
      },
      {
        state: {
          from: NAVIGATION_STATE.FROM_SCHEDULE_COMPONENT_LISTVIEW,
          locationGroupId: locationGroupId,
        },
      },
    );
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    payload: SelectedRow,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuContext(payload.context);
    selectedRowRef.current = payload;
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePublishClose = () => {
    publishModal.onCloseModal();
  };

  const location = useLocation();
  const isListActive = !!location?.pathname?.includes('/schedule');

  const handleDeleteConfirm = async () => {
    const payload = generateDeleteScheduleOrItemPayload(
      props?.locationGroup,
      props?.locationData,
      selectedRowRef.current,
    );

    await props.deleteBundleAction.mutateAsync(payload);

    handleMenuClose();

    props?.refetchSchedules?.();

    Snackbar.success(lang.successfullyDeleteScheduleAssembly);

    showDeleteScheduleOrItemsModal.onCloseModal();

    if (locationsCount > 0) {
      publishModal.onOpenModal();
    }
  };
  const isAllSelected = ![
    RowsPerPageOptions.TWENTY,
    RowsPerPageOptions.FIFTY,
    RowsPerPageOptions.ONE_HUNDRED,
  ].includes(props.rowsPerPage);
  const startIndex = (props.page - 1) * props.rowsPerPage + 1;
  const endIndex = Math.min(
    props.page * (isAllSelected ? Number.MAX_SAFE_INTEGER : props.rowsPerPage),
    props.totalCount,
  );
  const rowsPerPageMenu = useMenu();
  const rowsPerPageItems = [
    {
      label: String(RowsPerPageOptions.TWENTY),
      isSelected: props.rowsPerPage === RowsPerPageOptions.TWENTY,
      onClick: () => props.onRowsPerPageChange(RowsPerPageOptions.TWENTY),
    },
    {
      label: String(RowsPerPageOptions.FIFTY),
      isSelected: props.rowsPerPage === RowsPerPageOptions.FIFTY,
      onClick: () => props.onRowsPerPageChange(RowsPerPageOptions.FIFTY),
    },
    {
      label: String(RowsPerPageOptions.ONE_HUNDRED),
      isSelected: props.rowsPerPage === RowsPerPageOptions.ONE_HUNDRED,
      onClick: () => props.onRowsPerPageChange(RowsPerPageOptions.ONE_HUNDRED),
    },
    {
      label: RowsPerPageOptions.ALL as string,
      isSelected: isAllSelected,
      onClick: () => props.onRowsPerPageChange(99), // 99 per page to fetch all since the scheduleOverview end point only get back 100 items as max per page
    },
  ];

  const noDaypartMessage = getNoDaypartMessage(props.shouldShowListview);

  const handleSchedulePreview = (startDate?: string) => {
    openSchedulePreview({
      locationGroupId,
      daypartId: props.selectedDaypartId,
      date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
    });
  };

  return (
    <Container>
      <Header
        height={LOCATION_HEADER_HEIGHT}
        title={
          !!props.locationData
            ? props.locationData?.hq?.name || ' '
            : props.locationGroup?.name || ' '
        }
        actionBar={
          !isOwnerOperator && (locationGroups ?? []).length > 0 ? (
            <ActionBar
              showScheduler
              label={lang.scheduleComponent.label}
              isLocationGroupSchedule={props.isLocationGroupSchedule}
              onSchedulerHandleClick={sidebarDrawer.onOpenModal}
              sortTableData={sortTableData}
              headers={headers as string[]}
            />
          ) : (
            <ActionBarComponent
              showPreview
              previewDisabled={props.shouldShowListview?.previewDisable}
              onPreviewClick={() => handleSchedulePreview()}
              type={lang.preview}
            />
          )
        }
        breadcrumbItems={[
          {
            label: lang.locationGroup,
            link: !isOwnerOperator
              ? PATHS.LOCATION_GROUPS
              : PATHS.OO_LOCATION_PAGE,
          },
          {
            label: lang.schedules,
          },
        ]}
      />

      <DaypartDisabledAlert
        locationGroupId={Number(
          isLocationPage ? location_group_id : locationGroupId,
        )}
        {...(isLocationPage && { locationCode })}
        selectedDaypartId={Number(props.selectedDaypartId)}
        navigateTo={
          isLocationPage
            ? PATHS.LOCATION_TIMINGS(locationCode, location_group_id)
            : PATHS.LOCATION_GROUP_TIMINGS(locationGroupId)
        }
      />

      <CustomTableContainer>
        {!!location_group_id && !!props.locationData && (
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={Number(location_group_id)}
            onChange={props.onChangeLocationGroup}>
            {locationGroups?.map((item) => (
              <Tab
                disableRipple
                key={item.id}
                value={item?.id}
                label={item?.name}
              />
            ))}
          </Tabs>
        )}
        {props.locationData && (
          <Box height={LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT}>
            <LocationCalendarHeader
              selectedLocationGroup={locationGroups?.find(
                (group) => group.id === Number(location_group_id),
              )}
              scheduleDaypartsAvailable={!isEmpty(sortTableData)}
            />
          </Box>
        )}
        <HeaderContainer>
          <ButtonGroupWrapper>
            <StyledIconButton
              onClick={() =>
                props.locationData
                  ? navigate({
                      pathname: PATHS.LOCATION_CALENDAR(
                        props.locationData.location_code,
                      ),
                      search: `groupID=${location_group_id}`,
                    })
                  : navigate({
                      pathname: PATHS.LOCATION_GROUP_CALENDAR(locationGroupId),
                    })
              }>
              <CalendarTodayIcon data-testid="calendar-button" />
            </StyledIconButton>
            <StyledIconButton $active={isListActive}>
              <FormatListBulletedIcon data-testid="list-button" />
            </StyledIconButton>
          </ButtonGroupWrapper>
          {!props.isLocationGroupSchedule &&
            activeDays.some((day) => day.active) && (
              <ActiveDayIndicators activeDays={activeDays} />
            )}
        </HeaderContainer>
        <TopResultContainer>
          <Typography>
            {!props.locationGroupScheduleLoading &&
              `${lang.results}: ${
                props.tableData.length
                  ? `${startIndex}–${endIndex} / ${props.totalCount}`
                  : `${endIndex} / ${props.totalCount}`
              }`}
          </Typography>

          <DropdownContainer>
            <RoundedDropdown
              id="viewSelector"
              data-testid="view-dropdown"
              onChange={handleDropdownChange}
              value={props.selectedOption}
              options={LISTVIEW_DROPDOWN_OPTIONS}
              MenuProps={{
                PaperProps: {
                  sx: { mt: 1 },
                },
              }}>
              {LISTVIEW_DROPDOWN_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RoundedDropdown>
            {totalPositions && !isDefaultAssembly && (
              <RoundedDropdown
                id="positionDropdown"
                data-testid="position-dropdown"
                disabled={props.shouldShowListview?.daypartDisabled}
                value={props.selectedPositionId}
                onChange={(e: SelectChangeEvent<string>) =>
                  props.onPositionChange(e.target.value)
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                    },
                  },
                }}>
                {[...Array(totalPositions)].map((_, index) => {
                  const value = index + 1;

                  return (
                    <MenuItem key={value} value={value}>
                      {`Position ${value}`}
                    </MenuItem>
                  );
                })}
                <MenuItem value={DEFAULT_POSITION_ALL}>
                  {lang.scheduleComponent.positionDropdownAll}
                </MenuItem>
              </RoundedDropdown>
            )}
            <RoundedDropdown
              id="daypartSelector"
              data-testid="daypart-dropdown"
              disabled={props.shouldShowListview?.daypartDisabled}
              value={props.selectedDaypartId}
              onChange={(e: SelectChangeEvent<string>) =>
                props.onDaypartChange(Number(e.target.value))
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                  },
                },
              }}>
              {!props.isLoadingDayparts &&
                [...props.daypartOptions]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((daypart) => (
                    <MenuItem key={daypart.id} value={daypart.id}>
                      {daypart.name}
                    </MenuItem>
                  ))}
            </RoundedDropdown>
            <FlexRowCenter>
              <NoWrapTypography>{lang.itemsPerPage}</NoWrapTypography>
              <StyledLocationSortButton
                variant="text"
                endIcon={<UnfoldMoreIcon />}
                disableRipple
                disabled={
                  props.shouldShowListview?.daypartDisabled ||
                  props.shouldShowListview?.noScheduleComponent ||
                  props.shouldShowListview?.noDefaultAssembly
                }
                onClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                ) => rowsPerPageMenu.onOpenMenu(event.currentTarget)}>
                {rowsPerPageItems.find((item) => item.isSelected)?.label}
              </StyledLocationSortButton>
              <Menu
                anchorEl={rowsPerPageMenu.anchorEl}
                onClose={rowsPerPageMenu.onCloseMenu}
                open={Boolean(rowsPerPageMenu.anchorEl)}>
                {rowsPerPageItems.map((item) => (
                  <StyledPageMenuItem
                    role="menuitem"
                    key={item.label}
                    selected={item.isSelected}
                    onClick={() => {
                      item.onClick();
                      rowsPerPageMenu.onCloseMenu();
                    }}>
                    {item.label}
                  </StyledPageMenuItem>
                ))}
              </Menu>
            </FlexRowCenter>
          </DropdownContainer>
        </TopResultContainer>
        {noDaypartMessage ? (
          <BlankStateWrapper>
            <BlankStateImg
              src={EmptyIcon}
              alt="No records/daypart available"
              data-testid="emptyIcon"
              draggable={false}
            />
            <Typography variant="body2">{noDaypartMessage}</Typography>
          </BlankStateWrapper>
        ) : (
          <CustomTableWithPagination
            tableContainerHeight={getTableContainerHeight(!!locationGroupId)}
            overrideHeight={`calc(100% - ${PAGINATION_HEIGHT}px)`}
            columns={props.columns}
            rows={isDefaultAssembly ? sortTableData : props.tableData}
            loading={props.locationGroupScheduleLoading}
            page={props.page}
            totalPages={props.totalPage}
            onPageChange={props.onPageChange}
            tableHeadRenderer={(columns) => {
              const CellComponent = isDefaultAssembly
                ? CustomTableCell
                : CustomTableCellSchedule;

              return (
                <TableHead
                  data-testid={DataTestID.LocationSchedulePageTableHead}>
                  <TableRow>
                    {!isDefaultAssembly && (
                      <CustomTableCellSchedule
                        sx={{ width: '48px' }}
                        $isHeader
                      />
                    )}

                    {columns.map((column) => (
                      <CellComponent
                        key={column.id}
                        $isdoublewidth={column.id === 'name'}
                        $isHeader
                        sx={{
                          textAlign: (column.align || 'left') as any,
                          width: column.width,
                        }}>
                        <TableHeadCell
                          sx={{
                            display: 'flex',
                            justifyContent:
                              column.align === 'right'
                                ? 'flex-end'
                                : 'flex-start',
                          }}>
                          {column.label}
                          {column.sortIcon && (
                            <SortIcons>
                              <ArrowIcon
                                data-testid={`sort-${column.id}`}
                                onClick={() =>
                                  props.handleSortingClick(
                                    column.id,
                                    'ascending',
                                  )
                                }>
                                <SortArrowUpIconStyled
                                  active={
                                    props.sortDirection.key ===
                                      column.sortKey &&
                                    props.sortDirection.direction ===
                                      'ascending' &&
                                    props.sortDirection.isSortModified
                                  }
                                />
                              </ArrowIcon>
                              <ArrowIcon
                                onClick={() =>
                                  props.handleSortingClick(
                                    column.id,
                                    'descending',
                                  )
                                }>
                                <SortArrowDownIconStyled
                                  data-testid={`sort-down-${column.id}`}
                                  active={
                                    props.sortDirection.key ===
                                      column.sortKey &&
                                    props.sortDirection.direction ===
                                      'descending' &&
                                    props.sortDirection.isSortModified
                                  }
                                />
                              </ArrowIcon>
                            </SortIcons>
                          )}
                        </TableHeadCell>
                      </CellComponent>
                    ))}
                    {!isDefaultAssembly && (
                      <CustomTableCellSchedule
                        sx={{ width: '48px' }}
                        $isHeader
                      />
                    )}
                  </TableRow>
                </TableHead>
              );
            }}
            customCellRenderer={CustomCellRenderer}
            totalCount={props.totalCount}
            renderAccordionRow={
              !isDefaultAssembly
                ? (row, idx) => {
                    const isExpanded = expandedRow === idx;

                    return (
                      <TableRow key={idx}>
                        <TableCellContainer colSpan={props.columns.length + 2}>
                          <Accordion
                            disableGutters
                            expanded={isExpanded}
                            sx={{ p: 0, m: 0 }}
                            onChange={() => handleToggle(idx)}>
                            <StyledAccordionSummary>
                              <StyledTable>
                                <TableBody>
                                  <TableRow>
                                    <TableCell
                                      sx={{ width: '48px' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggle(idx);
                                      }}>
                                      <StyledExpandMoreIcon
                                        aria-label="expand more"
                                        $isExpanded={isExpanded}
                                      />
                                    </TableCell>
                                    {props.columns.map((col) => {
                                      const cellValue = row[col.id];
                                      const isOverrideColumn =
                                        col.id === 'override';
                                      const isNameColumn = col.id === 'name';

                                      return (
                                        <CustomTableCellSchedule
                                          key={col.id}
                                          align={col.align || 'left'}
                                          $isdoublewidth={col.id === 'name'}
                                          $isHeader={false}
                                          sx={{ width: col.width }}>
                                          {isOverrideColumn &&
                                          row.latest_override_date ? (
                                            <LocationOverrideChip
                                              chipName={
                                                lang.ownerOperatorOverride
                                              }
                                            />
                                          ) : isNameColumn ? (
                                            <CustomTooltip
                                              title={cellValue}
                                              overflowLineNumber={1}
                                              placement="bottom">
                                              <Typography>
                                                {cellValue}
                                              </Typography>
                                            </CustomTooltip>
                                          ) : (
                                            cellValue
                                          )}
                                        </CustomTableCellSchedule>
                                      );
                                    })}

                                    <StyledTableCell align="left">
                                      {shouldShowMenuIcon && (
                                        <IconButton
                                          aria-label="more"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuOpen?.(e, {
                                              scheduleId: row.id,
                                              scheduleName:
                                                row.assemblyName || row.name,
                                              scheduleItems: row.scheduleItems,
                                              context: MenuContext.SCHEDULE,
                                            });
                                          }}>
                                          <MoreVertIcon />
                                        </IconButton>
                                      )}
                                    </StyledTableCell>
                                  </TableRow>
                                </TableBody>
                              </StyledTable>
                            </StyledAccordionSummary>

                            <AccordionDetails>
                              <Box>
                                <StyledTable size="small">
                                  {props.expandedColumns && (
                                    <TableHead>
                                      <TableRow>
                                        <StyledTableCell />
                                        {props.expandedColumns.map((col) => (
                                          <TableCell
                                            key={col.id}
                                            align={col.align || 'left'}
                                            sx={{
                                              fontWeight: 600,
                                              py: 1.5,
                                              width: col.width,
                                            }}>
                                            {col.label}
                                          </TableCell>
                                        ))}
                                        <StyledTableCell />
                                      </TableRow>
                                    </TableHead>
                                  )}
                                  <TableBody>
                                    {(row.scheduleItems || []).map(
                                      (item: any, iIdx: number) => {
                                        const hasOverrides =
                                          Array.isArray(item.overrides) &&
                                          item.overrides.length > 0 &&
                                          location_group_id;

                                        return (
                                          <>
                                            <TableRow key={iIdx}>
                                              <StyledTableCell />
                                              {(props.expandedColumns
                                                ? props.expandedColumns
                                                : props.columns
                                              ).map((col) => {
                                                const cellValue =
                                                  col.id === 'name' &&
                                                  CustomCellRenderer?.[
                                                    'componentName'
                                                  ]
                                                    ? CustomCellRenderer[
                                                        'componentName'
                                                      ](item, {
                                                        id: 'component_name',
                                                      })
                                                    : item[col.id];

                                                return (
                                                  <CustomTableCellSchedule
                                                    key={col.id}
                                                    align={col.align || 'left'}
                                                    $isdoublewidth={
                                                      col.id === 'name'
                                                    }
                                                    $isHeader={false}
                                                    sx={{
                                                      py: 1.5,
                                                      width: col.width,
                                                    }}>
                                                    {col.id === 'override' &&
                                                    item.allow_overrides ? (
                                                      <OverrideEnabledChip>
                                                        <OverrideEnabledIcon />
                                                        {lang.cmOverrideEnabled}
                                                      </OverrideEnabledChip>
                                                    ) : (
                                                      cellValue
                                                    )}
                                                  </CustomTableCellSchedule>
                                                );
                                              })}
                                              <StyledTableCell align="left">
                                                {locationGroupId &&
                                                  !isViewOnly && (
                                                    <IconButton
                                                      aria-label="expand component"
                                                      onClick={(e) =>
                                                        handleMenuOpen?.(e, {
                                                          scheduleId: row.id,
                                                          scheduleName:
                                                            row.name,
                                                          scheduleItemId:
                                                            item.id,
                                                          componentName:
                                                            item.component_name ||
                                                            item.componentName,
                                                          context:
                                                            MenuContext.COMPONENT,
                                                        })
                                                      }>
                                                      <MoreVertIcon />
                                                    </IconButton>
                                                  )}
                                              </StyledTableCell>
                                            </TableRow>
                                            {hasOverrides &&
                                              item.overrides.map(
                                                (
                                                  override: any,
                                                  oIdx: number,
                                                ) => (
                                                  <TableRow
                                                    key={`override-${override.id}-${oIdx}`}>
                                                    {/* Align with expand icon */}
                                                    <StyledTableCell />

                                                    {/* Override columns */}
                                                    {(props.expandedColumns
                                                      ? props.expandedColumns
                                                      : props.columns
                                                    ).map((col) => (
                                                      <CustomTableCellSchedule
                                                        key={col.id}
                                                        align={
                                                          col.align || 'left'
                                                        }
                                                        $isdoublewidth={
                                                          col.id === 'name'
                                                        }
                                                        $isHeader={false}
                                                        sx={{
                                                          py: 1.5,
                                                          width: col.width,
                                                        }}>
                                                        {col.id ===
                                                        'override' ? (
                                                          <LocationOverrideChip
                                                            chipName={
                                                              lang.ownerOperatorOverride
                                                            }
                                                          />
                                                        ) : col.id ===
                                                          'latest_override_date' ? (
                                                          override.created_at ??
                                                          ''
                                                        ) : col.id ===
                                                          'name' ? (
                                                          <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={0.5}>
                                                            <Box
                                                              display="flex"
                                                              alignItems="center"
                                                              gap={0.5}>
                                                              <SubdirectoryArrowRight
                                                                fontSize="small"
                                                                sx={{
                                                                  color:
                                                                    'primary.main',
                                                                }}
                                                              />
                                                              {override.component_id
                                                                ? CustomCellRenderer?.componentName
                                                                  ? CustomCellRenderer.componentName(
                                                                      override,
                                                                      {
                                                                        id: 'component_name',
                                                                      },
                                                                    )
                                                                  : override[
                                                                      col.id
                                                                    ] ?? ''
                                                                : override.component_name ??
                                                                  override[
                                                                    col.id
                                                                  ] ??
                                                                  ''}
                                                            </Box>
                                                          </Box>
                                                        ) : (
                                                          override[col.id] ?? ''
                                                        )}
                                                      </CustomTableCellSchedule>
                                                    ))}

                                                    {/* No menu icon for overrides */}
                                                    <StyledTableCell />
                                                  </TableRow>
                                                ),
                                              )}
                                          </>
                                        );
                                      },
                                    )}
                                  </TableBody>
                                </StyledTable>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        </TableCellContainer>
                      </TableRow>
                    );
                  }
                : undefined
            }
          />
        )}
      </CustomTableContainer>

      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        data-testid={DataTestID.LocationSchedulePageTableDeleteMenu}
        onClose={handleMenuClose}>
        {menuContext === MenuContext.SCHEDULE && (
          <>
            {isViewOnly ? (
              <StyledMenuItem
                onClick={() => {
                  handleMenuClose();
                  const earliestStartDate = getEarliestStartDate(
                    selectedRowRef.current.scheduleItems as IScheduleItem[],
                  );
                  handleSchedulePreview(earliestStartDate ?? undefined);
                }}>
                <PlayCircleOutlinedIcon fontSize="small" />
                {lang.preview}
              </StyledMenuItem>
            ) : (
              <>
                <StyledMenuItem onClick={handleDeleteClick}>
                  <DeleteIcon fontSize="small" />
                  {lang.deleteSchedule}
                </StyledMenuItem>
                {locationGroupId && (
                  <>
                    <StyledMenuItem onClick={handleEditClick}>
                      <EditIcon fontSize="small" />
                      {lang.editSchedule}
                    </StyledMenuItem>
                    <StyledMenuItem
                      disabled={
                        !isPreviewEnabled(
                          selectedRowRef.current
                            .scheduleItems as IScheduleItem[],
                        )
                      }
                      onClick={() => {
                        handleMenuClose();
                        const earliestStartDate = getEarliestStartDate(
                          selectedRowRef.current
                            .scheduleItems as IScheduleItem[],
                        );
                        handleSchedulePreview(earliestStartDate ?? undefined);
                      }}>
                      <PlayCircleOutlinedIcon fontSize="small" />
                      {lang.preview}
                    </StyledMenuItem>
                  </>
                )}
              </>
            )}
          </>
        )}
        {menuContext === MenuContext.COMPONENT && (
          <StyledMenuItem onClick={handleDeleteClick}>
            <DeleteIcon fontSize="small" />
            {lang.deleteScheduleItemModal.title}
          </StyledMenuItem>
        )}
      </Menu>

      {showDeleteScheduleOrItemsModal.open && (
        <SimpleDialog
          open={showDeleteScheduleOrItemsModal.open}
          cancelText={lang.cancel}
          confirmText={lang.deleteText}
          title={
            selectedRowRef.current.scheduleItemId
              ? lang.deleteScheduleItemModal.title
              : lang.deleteScheduleComponentModal.title
          }
          subTitle={
            selectedRowRef.current.scheduleItemId
              ? locationsCount > 0
                ? lang.deleteScheduleItemModal.subTitleWithOverridesWarning(
                    selectedRowRef.current?.componentName,
                  )
                : lang.deleteScheduleItemModal.subTitleWithOutOverridesWarning(
                    selectedRowRef.current?.componentName,
                  )
              : locationsCount > 0
              ? lang.deleteScheduleComponentModal.subTitleWithOverridesWarning(
                  selectedRowRef.current?.scheduleName,
                )
              : lang.deleteScheduleComponentModal.subTitleWithOutOverridesWarning(
                  selectedRowRef.current?.scheduleName,
                )
          }
          onClose={showDeleteScheduleOrItemsModal.onCloseModal}
          onClickCancel={() => {
            showDeleteScheduleOrItemsModal.onCloseModal();
            handleMenuClose();
          }}
          modalSize="sm"
          isLoading={props.deleteBundleAction.isLoading}
          onClickConfirm={showDeleteScheduleOrItemsModal.customFn}
          actionColor="error"
        />
      )}

      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          {...(isLocationPage
            ? { locationCodes: [locationCode] }
            : {
                locationGroupIDs: props.locationGroup
                  ? [props.locationGroup.id]
                  : [],
              })}
          onClose={handlePublishClose}
          publishMessage={lang.successfullyPublished}
        />
      )}
    </Container>
  );
};
